import { Link } from "react-router-dom";
import { useState } from "react";
import VoteWidget from "./VoteWidget";
import ModMenu from "./ModMenu";
import ReportModal from "./ReportModal";
import TranslatedText from "./TranslatedText";
import { timeAgo } from "../lib/api";
import { ChatCircle, Eye, CheckCircle, Lock, PushPin } from "@phosphor-icons/react";

export default function FeedItem({ item, index = 0, onChange }) {
    const isQuestion = item.kind === "question" || item.question_id;
    const id = isQuestion ? item.question_id : item.post_id;
    const targetType = isQuestion ? "question" : "post";
    const linkTo = isQuestion ? `/q/${item.question_id}` : `/p/${item.post_id}`;
    const delay = Math.min(index, 4);
    const [reportOpen, setReportOpen] = useState(false);
    const isDeleted = !!item.deleted_at;

    return (
        <>
            <article
                className={`group hc-card hc-card-interactive ${item.pinned ? "ring-1 ring-amber-500/30" : ""} animate-fadein-${delay} ${isDeleted ? "opacity-50" : ""}`}
                data-testid={`feed-item-${id}`}
            >
                <div className="flex">
                    <div className="flex flex-col items-center justify-start gap-1 p-2 sm:p-3 bg-white/[0.02] rounded-l-[0.85rem] border-r border-white/5">
                        <VoteWidget targetType={targetType} targetId={id} initialScore={item.score || 0} />
                    </div>
                    <div className="flex-1 p-3.5 sm:p-4 min-w-0">
                        <div className="flex flex-nowrap sm:flex-wrap items-center gap-1.5 sm:gap-2 mb-2 text-[10px] uppercase tracking-widest text-slate-500 min-w-0 overflow-hidden">
                            {item.pinned && (
                                <span className="shrink-0 whitespace-nowrap text-amber-400 flex items-center gap-1"><PushPin size={11} weight="fill" /> pinned</span>
                            )}
                            {item.locked && (
                                <span className="shrink-0 whitespace-nowrap text-rose-400 flex items-center gap-1"><Lock size={11} weight="fill" /> locked</span>
                            )}
                            {isQuestion ? (
                                <span className="shrink-0 text-cyan-400 border border-cyan-500/30 px-1.5 py-0.5 bg-cyan-500/5 rounded-sm">Q&amp;A</span>
                            ) : (
                                <Link to={`/h/${item.subforum_slug}`} className="shrink-0 whitespace-nowrap text-purple-400 border border-purple-500/30 px-1.5 py-0.5 bg-purple-500/5 rounded-sm hover:bg-purple-500/10" data-testid={`feed-sub-${item.subforum_slug}`}>
                                    h/{item.subforum_slug}
                                </Link>
                            )}
                            <span className="max-sm:hidden">by</span>
                            <Link to={`/u/${item.author_username}`} className="text-slate-300 hover:text-cyan-400 truncate max-w-[14ch] sm:max-w-none" data-testid={`feed-author-${item.author_username}`}>
                                {item.author_username}
                            </Link>
                            <span className="shrink-0">·</span>
                            <span className="shrink-0 whitespace-nowrap">{timeAgo(item.created_at)}</span>
                            {item.accepted_answer_id && (
                                <span className="shrink-0 whitespace-nowrap text-emerald-400 flex items-center gap-1 ml-1">
                                    <CheckCircle size={11} weight="fill" /> solved
                                </span>
                            )}
                            <div className="ml-auto shrink-0 pl-1">
                                <ModMenu
                                    targetType={targetType}
                                    targetId={id}
                                    isLocked={item.locked}
                                    isPinned={item.pinned}
                                    isDeleted={isDeleted}
                                    onChange={onChange}
                                    onReport={() => setReportOpen(true)}
                                />
                            </div>
                        </div>
                        <Link to={linkTo} data-testid={`feed-title-${id}`}>
                            <h3 className={`font-bold text-base sm:text-lg ${isDeleted ? "line-through text-slate-500" : "text-slate-100 group-hover:text-cyan-300"} transition-colors tracking-tight leading-snug mb-2 line-clamp-2 sm:line-clamp-none`} style={{ fontFamily: "'Space Grotesk', 'Chivo', sans-serif" }}>
                                <TranslatedText text={item.title} />
                            </h3>
                        </Link>
                        {item.flex && !isDeleted ? (
                            <Link to={linkTo} className="block mb-3" data-testid={`flex-card-preview-${id}`}>
                                <img
                                    src={`${process.env.REACT_APP_BACKEND_URL}${(item.body?.match(/\((\/api\/streak-cards\/[^)]+)\)/) || [])[1] || ""}`}
                                    alt="flex card"
                                    loading="lazy"
                                    className="rounded-sm border border-orange-500/25 max-w-md w-full"
                                />
                            </Link>
                        ) : (
                            <p className="text-sm text-slate-400 line-clamp-2 mb-3">
                                {isDeleted ? "[content removed by moderator]" : <TranslatedText text={item.body?.substring(0, 240)} />}
                            </p>
                        )}
                        <div className="flex items-center justify-between flex-wrap gap-2">
                            <div className="flex flex-wrap gap-1.5">
                                {(item.tags || []).map((t) => (
                                    <Link
                                        key={t}
                                        to={`/?tag=${t}`}
                                        className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-slate-800 text-slate-300 border border-slate-700 hover:border-cyan-500/40 hover:text-cyan-400 rounded-sm"
                                        data-testid={`feed-tag-${t}`}
                                    >
                                        #{t}
                                    </Link>
                                ))}
                            </div>
                            <div className="flex items-center gap-3 text-[11px] text-slate-500 font-mono">
                                <span className="flex items-center gap-1">
                                    <ChatCircle size={12} /> {item.answer_count ?? item.comment_count ?? 0}
                                </span>
                                {item.flex && (
                                    <span className="flex items-center gap-1 text-orange-300" data-testid={`feed-fire-count-${id}`}>
                                        🔥 {item.fire_count || 0}
                                    </span>
                                )}
                                {isQuestion && (
                                    <span className="flex items-center gap-1">
                                        <Eye size={12} /> {item.view_count || 0}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </article>
            <ReportModal open={reportOpen} onClose={() => setReportOpen(false)} targetType={targetType} targetId={id} />
        </>
    );
}
